import { DAIEngineProfile, DAIEngineProfile_ConfigEditability } from '../gen/ai/h2o/engine/v1/dai_engine_profile_pb';
import {
  CreateDAIEngineProfileRequest,
  CreateDAIEngineProfileResponse,
  DAIEngineProfileService_CreateDAIEngineProfile,
  DAIEngineProfileService_DeleteDAIEngineProfile,
  DAIEngineProfileService_GetDAIEngineProfile,
  DAIEngineProfileService_ListDAIEngineProfiles,
  DAIEngineProfileService_UpdateDAIEngineProfile,
  DeleteDAIEngineProfileRequest,
  DeleteDAIEngineProfileResponse,
  GetDAIEngineProfileRequest,
  GetDAIEngineProfileResponse,
  ListDAIEngineProfilesRequest,
  ListDAIEngineProfilesResponse,
  UpdateDAIEngineProfileRequest,
  UpdateDAIEngineProfileResponse,
} from '../gen/ai/h2o/engine/v1/dai_engine_profile_service_pb';
import { BigIntString } from '../gen/runtime';
import {
  engineProfileBaseFields,
  engineProfileFieldsConfig,
  engineProfilesColumns,
  EntityActionType,
  EntityType,
} from './constants';
import { Entity, EntityAction, EntityField } from './types';

const entityType = EntityType.DAIEngineProfile;

export const actions = {
  [EntityActionType.Create]: {
    name: 'CreateDAIEngineProfile',
    rpc: DAIEngineProfileService_CreateDAIEngineProfile,
    type: EntityActionType.Create,
    responseKey: 'daiEngineProfile',
    requestNameKey: 'daiEngineProfileId',
    requestPayloadKey: 'daiEngineProfile',
    requestNameKeyPrefix: 'daiEngineProfile/',
    entityType,
  } as EntityAction<DAIEngineProfile, CreateDAIEngineProfileRequest, CreateDAIEngineProfileResponse>,
  [EntityActionType.Get]: {
    name: 'GetDAIEngineProfile',
    rpc: DAIEngineProfileService_GetDAIEngineProfile,
    type: EntityActionType.Get,
    requestNameKey: 'name' as unknown,
    responseKey: 'daiEngineProfile',
    entityType,
  } as EntityAction<DAIEngineProfile, GetDAIEngineProfileRequest, GetDAIEngineProfileResponse>,
  [EntityActionType.List]: {
    name: 'ListDAIEngineProfile',
    rpc: DAIEngineProfileService_ListDAIEngineProfiles,
    type: EntityActionType.List,
    responseKey: 'daiEngineProfiles',
    entityType,
  } as EntityAction<DAIEngineProfile, ListDAIEngineProfilesRequest, ListDAIEngineProfilesResponse>,
  [EntityActionType.Update]: {
    name: 'UpdateDAIEngineProfile',
    rpc: DAIEngineProfileService_UpdateDAIEngineProfile,
    type: EntityActionType.Update,
    responseKey: 'daiEngineProfile',
    requestNameKey: 'name' as unknown,
    requestPayloadKey: 'daiEngineProfile',
    entityType,
  } as EntityAction<DAIEngineProfile, UpdateDAIEngineProfileRequest, UpdateDAIEngineProfileResponse>,
  [EntityActionType.Delete]: {
    name: 'DeleteDAIEngineProfile',
    rpc: DAIEngineProfileService_DeleteDAIEngineProfile,
    type: EntityActionType.Delete,
    entityType,
  } as EntityAction<DAIEngineProfile, DeleteDAIEngineProfileRequest, DeleteDAIEngineProfileResponse>,
};

const engineProfileFields = engineProfileFieldsConfig<DAIEngineProfile>('DAI');

export const fields: EntityField<DAIEngineProfile>[] = [
  ...engineProfileBaseFields<DAIEngineProfile>('DAI'),
  engineProfileFields.storageBytesConstraint,
  engineProfileFields.maxIdleDurationConstraint,
  engineProfileFields.maxRunningDurationConstraint,
  engineProfileFields.maxNonInteractionDuration,
  engineProfileFields.maxUnusedDuration,
  engineProfileFields.maxRunningEngines,
  engineProfileFields.tritonEnabled,
  engineProfileFields.configEditability,
  engineProfileFields.baseConfiguration,
  engineProfileFields.configurationOverride,
  engineProfileFields.yamlPodTemplateSpec,
  engineProfileFields.yamlGpuTolerations,
  engineProfileFields.gpuResourceName,
  engineProfileFields.dataDirectoryStorageClass,
];

export const daiEngineProfileEntity: Entity<DAIEngineProfile> = {
  type: EntityType.DAIEngineProfile,
  name: 'daiEngineProfiles',
  createButtonLabel: 'Add DAI Engine Profile',
  displayName: 'DAI Engine Profile',
  displayNameKey: 'displayName',
  actions,
  columns: engineProfilesColumns,
  fields,
  requestNameKeyPrefix: 'workspaces/global/daiEngineProfiles/',
  emptyModel: {
    name: 'new-dai-engine-profile-id',
    displayName: 'New DAI Engine Profile',
    priority: 100,
    enabled: true,
    assignedOidcRolesEnabled: false,
    assignedOidcRoles: [],
    maxRunningEngines: undefined,
    cpuConstraint: {
      min: '1' as BigIntString,
      default: '2' as BigIntString,
    },
    gpuConstraint: {
      min: '0' as BigIntString,
      default: '0' as BigIntString,
    },
    memoryBytesConstraint: {
      min: '4294967296' as BigIntString,
      default: '8589934592' as BigIntString,
    },
    storageBytesConstraint: {
      min: '4294967296' as BigIntString,
      default: '8589934592' as BigIntString,
    },
    maxIdleDurationConstraint: {
      min: '3600s' as BigIntString,
      default: '3600s' as BigIntString,
    },
    maxRunningDurationConstraint: {
      min: '3600s' as BigIntString,
      default: '432000s' as BigIntString,
    },
    maxNonInteractionDuration: undefined,
    maxUnusedDuration: undefined,
    configurationOverride: {},
    baseConfiguration: {},
    configEditability: DAIEngineProfile_ConfigEditability.FULL,
    yamlPodTemplateSpec: '',
    yamlGpuTolerations: '',
    tritonEnabled: false,
  },
  canBeReordered: false,
};
